import { Sparkles } from "lucide-react";
import {
  CrmContactAiSuggestionsCard,
  type CrmContactAiSuggestion,
  type CrmContactAiSuggestionStatus,
} from "./contact-ai-suggestions-card";

interface CrmContactAiInsightsPanelProps {
  summary: string | null;
  isSummarizing: boolean;
  suggestedStage: string | null;
  suggestedConfidence: number | null;
  isStageSuggestionLoading: boolean;
  suggestions: CrmContactAiSuggestion[];
  suggestionStatuses: Record<string, CrmContactAiSuggestionStatus>;
  isGeneratingSuggestions: boolean;
  error: string | null;
  onGenerateSummary: () => void;
  onSuggestStage: () => void;
  onGenerateSuggestions: () => void;
  onSuggestionDecision: (
    suggestion: CrmContactAiSuggestion,
    index: number,
    action: CrmContactAiSuggestionStatus,
  ) => void;
}

export function CrmContactAiInsightsPanel({
  summary,
  isSummarizing,
  suggestedStage,
  suggestedConfidence,
  isStageSuggestionLoading,
  suggestions,
  suggestionStatuses,
  isGeneratingSuggestions,
  error,
  onGenerateSummary,
  onSuggestStage,
  onGenerateSuggestions,
  onSuggestionDecision,
}: CrmContactAiInsightsPanelProps) {
  return (
    <div className="mt-4 space-y-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => { onGenerateSummary(); }}
          disabled={isSummarizing}
          className="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border)] px-3 py-1.5 text-xs text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-background)] disabled:opacity-50"
        >
          <Sparkles className="h-3.5 w-3.5" />
          {isSummarizing ? "Generating summary..." : "Generate AI Summary"}
        </button>
        <button
          type="button"
          onClick={() => { onSuggestStage(); }}
          disabled={isStageSuggestionLoading}
          className="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border)] px-3 py-1.5 text-xs text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-background)] disabled:opacity-50"
        >
          <Sparkles className="h-3.5 w-3.5" />
          {isStageSuggestionLoading ? "Checking stage..." : "Suggest Lifecycle Stage"}
        </button>
        <button
          type="button"
          onClick={() => { onGenerateSuggestions(); }}
          disabled={isGeneratingSuggestions}
          className="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-ai-border)] bg-[var(--color-ai-light)] px-3 py-1.5 text-xs text-[var(--color-ai)] transition-colors hover:bg-[var(--color-background)] disabled:opacity-50"
        >
          <Sparkles className="h-3.5 w-3.5" />
          {isGeneratingSuggestions ? "Generating suggestions..." : "AI Suggestions"}
        </button>
      </div>

      {summary ? (
        <div className="rounded-md bg-[var(--color-background)] px-3 py-2 text-xs text-[var(--color-foreground)]">
          <p className="mb-1 font-medium text-[var(--color-ai)]">AI Contact Summary</p>
          <p>{summary}</p>
        </div>
      ) : null}

      {suggestedStage ? (
        <div className="rounded-md bg-[var(--color-background)] px-3 py-2 text-xs text-[var(--color-foreground)]">
          <p className="mb-1 font-medium text-[var(--color-ai)]">Suggested Lifecycle Stage</p>
          <p>
            {suggestedStage}
            {suggestedConfidence != null ? ` (${suggestedConfidence}% confidence)` : ""}
          </p>
        </div>
      ) : null}

      <CrmContactAiSuggestionsCard
        suggestions={suggestions}
        statuses={suggestionStatuses}
        onDecision={onSuggestionDecision}
      />

      {error ? (
        <p className="text-xs text-[var(--color-destructive)]">{error}</p>
      ) : null}
    </div>
  );
}
